import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/servizi", label: "Servizi" },
    { to: "/shop", label: "Kit" },
    { to: "/#come-funziona", label: "Come funziona" },
    { to: "/#filosofia", label: "Laboratorio" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#F8F5EF]/92 backdrop-blur-xl border-b border-[#DED5C3]" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-16 flex items-center justify-between h-24">
        <Link to="/" className="flex items-baseline gap-3" data-testid="brand-logo">
          <span className="font-serif text-2xl tracking-tight leading-none">Premium</span>
          <span className="overline text-[#A87D4A] text-[0.62rem]">Shoes Care</span>
          <span className="hidden xl:inline text-[0.72rem] text-[#8A8276] ml-1 italic font-serif">by M.C. Palombi</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-12">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-[0.75rem] uppercase tracking-[0.22em] transition-colors link-underline font-medium ${isActive ? "text-[#1A1612]" : "text-[#5E564C] hover:text-[#1A1612]"}`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <Link to="/carrello" className="relative p-1.5 hover:text-[#A87D4A] transition-colors" data-testid="cart-button" aria-label="Carrello">
            <ShoppingBag strokeWidth={1.3} size={20} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#A87D4A] text-white text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user && user.id ? (
            <div className="hidden md:flex items-center gap-5">
              {user.role === "admin" && (
                <Link to="/admin" className="text-[0.75rem] uppercase tracking-[0.2em] text-[#5E564C] hover:text-[#1A1612] flex items-center gap-1.5" data-testid="admin-link">
                  <LayoutDashboard size={14} strokeWidth={1.3} /> Admin
                </Link>
              )}
              <Link to="/ordini" className="text-[0.75rem] uppercase tracking-[0.2em] text-[#5E564C] hover:text-[#1A1612]" data-testid="my-orders-link">Ordini</Link>
              <button onClick={() => { logout(); nav("/"); }} className="text-[#5E564C] hover:text-[#A87D4A]" data-testid="logout-btn" aria-label="Logout">
                <LogOut size={16} strokeWidth={1.3} />
              </button>
            </div>
          ) : (
            <Link to="/accedi" className="hidden md:inline-flex items-center gap-1.5 text-[0.75rem] uppercase tracking-[0.22em] text-[#5E564C] hover:text-[#1A1612] font-medium" data-testid="login-link">
              <User size={14} strokeWidth={1.3} /> Accedi
            </Link>
          )}

          <button className="lg:hidden p-1.5" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#DED5C3] px-6 py-6 bg-[#F8F5EF]" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-3 text-[#1A1612] uppercase tracking-[0.2em] text-xs font-medium">{l.label}</Link>
          ))}
          <div className="hairline-gold my-4" />
          {user && user.id ? (
            <>
              <Link to="/ordini" onClick={() => setOpen(false)} className="block py-2 uppercase tracking-[0.2em] text-xs">Ordini</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block py-2 uppercase tracking-[0.2em] text-xs">Admin</Link>}
              <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block py-2 text-left uppercase tracking-[0.2em] text-xs">Esci</button>
            </>
          ) : (
            <Link to="/accedi" onClick={() => setOpen(false)} className="block py-2 uppercase tracking-[0.2em] text-xs">Accedi</Link>
          )}
        </div>
      )}
    </header>
  );
}
